import { useQuery } from '@tanstack/react-query';
import type { ReturnYoutubeDislikeSettings } from '@visious/core';
import { fetchReturnYoutubeDislikeVotes } from '../../api/returnYoutubeDislike';
import { getPlatform } from '../../platform/instance';

/**
 * The Return YouTube Dislike half of the Watch screen: one lookup for the currently playing video,
 * `useSponsorBlock`'s own shape — its own hook because the state is nobody else's, while the
 * wire-format reading stays in core (`returnyoutubedislike/parse.ts`).
 *
 * Keyed by service as well as video, like the other two integrations: a self-hosted server is a
 * different database, not a stale copy of the official one. Failure collapses to "no figure" —
 * the like count keeps rendering alone, exactly as it did before this integration existed.
 */
export function useReturnYoutubeDislike(
  videoId: string,
  settings: ReturnYoutubeDislikeSettings,
): number | null {
  const query = useQuery({
    queryKey: ['returnYoutubeDislike', settings.apiUrl, videoId],
    enabled: settings.enabled,
    retry: false,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const boot = await getPlatform();
      if (!boot.ok) return null;
      const result = await fetchReturnYoutubeDislikeVotes(boot.platform, settings, videoId);
      return result.ok ? result.value.dislikeCount : null;
    },
  });

  return settings.enabled ? (query.data ?? null) : null;
}
